import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { EmailEditorComponent } from 'angular-email-editor';

import { ALLOWED_IMAGE_UPLOAD_TYPES } from '@sb-shared/components/communication-template-manager/image-upload-types';

import { designFingerprint } from './design-fingerprint';

/** Result of an Unlayer export: the design JSON plus the rendered HTML. */
export interface TemplateEditorExport {
  design: object;
  html: string;

  /**
   * Where the canvas stood when the export was taken. Set on every export the editor hands out, and read by
   * the editor alone, when a host reports a save of the export ({@link TemplateEditorComponent.markSaved}). It
   * travels with the export, so a copy of the export counts as the export (SB-17105). An export made anywhere
   * else has none, and the editor ignores it.
   */
  readonly revision?: ExportRevision;

  /**
   * Set when images Unlayer stored itself were taken out of this export, so it does not hold everything the
   * canvas shows (SB-17176). The editor has said why through `imageUploadFailed` just before.
   */
  readonly imagesRemoved?: boolean;
}

declare const exportRevision: unique symbol;

/** Opaque outside the editor: only the editor makes one, and only the editor reads it. */
export interface ExportRevision {
  readonly [exportRevision]: never;
}

/** What an {@link ExportRevision} holds: the load the export belongs to, and the edits it takes in. */
interface CanvasRevision {
  readonly generation: number;
  readonly seq: number;
}

/**
 * The last load generation handed out. One sequence for every editor on the page, so a revision taken in one
 * editor never passes for one of another's.
 */
let lastLoadGeneration = 0;

/**
 * How long after a programmatic `loadDesign`/`loadBlank` Unlayer's `design:updated` is still treated as
 * an echo of that load rather than a user edit. Unlayer gives no completion callback for a load in this
 * embed, so the window is the only available signal; generous enough for a large legacy body to settle.
 */
const PROGRAMMATIC_LOAD_SETTLE_MS = 1000;

/** How long after the last edit the canvas is compared with the saved design (see {@link TemplateEditorComponent.canvasDirtyChange}). */
const REVERT_CHECK_DEBOUNCE_MS = 300;

/** Whether `url` appears anywhere in an Unlayer design, compared in JSON form so that escaping cannot hide it. */
function designHolds(design: object, url: string): boolean {
  return JSON.stringify(design ?? {}).includes(JSON.stringify(url).slice(1, -1));
}

/** How HTML can spell `url`: as an attribute writes it, with `&` as `&amp;`, and as it is. */
function htmlSpellings(url: string): string[] {
  return [url.replace(/&/g, '&amp;'), url];
}

/**
 * `html` without the `<img>` tags showing one of `urls`, so an email shows no broken image, and with any other
 * mention of them blanked. Each URL is matched in every {@link htmlSpellings spelling}.
 */
function withoutImagesInHtml(html: string, urls: readonly string[]): string {
  const attributes = `(?:[^>"']|"[^"]*"|'[^']*')*`;
  return urls.flatMap(htmlSpellings).reduce((text, url) => {
    const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tag = new RegExp(`<img\\b${attributes}?\\bsrc=(["'])${escaped}\\1${attributes}>`, 'gi');
    return text.replace(tag, '').split(url).join('');
  }, html);
}

/**
 * `design` without the blocks whose image is one of `urls`, so a later export cannot render an empty image
 * in their place, and with any other mention of them taken out (an HTML block's markup, a background).
 */
function withoutImages(design: object, urls: readonly string[]): object {
  const shows = (node: unknown): boolean =>
    urls.includes((node as { values?: { src?: { url?: string } } } | null)?.values?.src?.url ?? '');
  const prune = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      return node.filter(item => !shows(item)).map(prune);
    }
    if (node && typeof node === 'object') {
      return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, prune(value)]));
    }
    return typeof node === 'string' ? withoutImagesInHtml(node, urls) : node;
  };
  return prune(design) as object;
}

/**
 * `sb-template-editor` — native Angular wrapper around the Unlayer email editor
 * (SB-14943, Increment G1). Mirrors the `sb-textarea` pattern: a thin wrapper over a
 * third-party editor.
 *
 * Surfaces BOTH the design JSON (`designChange`) and the rendered HTML (`htmlExport`)
 * so the management UI can persist them together (plan R5) and the preview can render
 * the current HTML (G7). Merge tags are supplied by the parent (G2 `MergeTags`
 * endpoint) and each tag's `value` must already be the legacy `~~{TOKEN}~~` form
 * (plan D5 / R2) so the backend renderer substitutes it.
 */
@Component({
  selector: 'sb-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  standalone: false
})
export class TemplateEditorComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Unlayer's loader script failed in this page session. The editor library then treats the script as
   * injected and never loads it again, so no later editor can start until the page reloads. Hosts with a
   * fallback read this to use it at once instead of waiting out {@link loadTimeoutMs}. A timeout is not
   * remembered: the script may still arrive.
   */
  static loaderScriptFailed = false;

  /** Unlayer design JSON to load for editing. Null/undefined => blank canvas (new template). */
  @Input() design: object | null;

  /**
   * Unlayer body values for a blank canvas (content width, background, font…). Undefined leaves Unlayer's
   * own defaults: 500 px wide on a grey background.
   */
  @Input() blankBodyValues?: Record<string, unknown>;

  /**
   * How long Unlayer may take to become ready before {@link loadFailed} is emitted, counted only while the
   * page is visible. Unset: no limit, which is what the admin screens rely on.
   */
  @Input() loadTimeoutMs?: number;

  /**
   * Merge-tag catalogue in Unlayer `mergeTags` shape. Each tag's `value` MUST be the
   * legacy `~~{TOKEN}~~` form (plan D5/R2). Supplied by the G2 `MergeTags` endpoint.
   *
   * Unset leaves Unlayer to offer the merge tags set up in the Unlayer project; `{}` offers none.
   */
  @Input() mergeTags: unknown;

  /** Unlayer project id (paid features / saved blocks). Optional in dev. */
  @Input() projectId?: number;

  /** Minimum editor height (Unlayer expects a CSS length string, e.g. `40rem`). */
  @Input() minHeight = '40rem';

  /**
   * Override for the Unlayer loader script URL (default `https://editor.unlayer.com/embed.js`).
   * Point at a self-hosted copy (e.g. `assets/unlayer/embed.js`) to avoid loading it from the CDN.
   * Undefined ⇒ the editor uses its default CDN URL.
   */
  @Input() scriptUrl?: string;

  /** Extra Unlayer options merged last (appearance, tools, locale, …). */
  @Input() options: Record<string, unknown> = {};

  /**
   * Optional image-upload handler: stores an image the author uploads (e.g. in SchoolsBuddy storage) and returns
   * its URL. Unlayer hands it the file only where the Unlayer project is applied for the domain. Elsewhere it
   * stores the file itself: such an image is taken back off the canvas while it is the last change, and kept
   * out of every export (SB-17176, see {@link takeOutImagesUnlayerStored}).
   */
  @Input() imageUploader?: (file: File) => Promise<string>;

  /** Emits the design JSON whenever it is saved or exported. */
  @Output() designChange = new EventEmitter<object>();

  /**
   * Emits the design + rendered HTML together (save / preview). A host that stores it says so with
   * {@link saveStarted}, then {@link markSaved} or {@link saveFailed}. Images Unlayer stored itself are not in
   * it ({@link TemplateEditorExport.imagesRemoved}).
   */
  @Output() htmlExport = new EventEmitter<TemplateEditorExport>();

  /** Emits once Unlayer has fired `(ready)` and the initial design is loaded, so callers can gate export/preview/save until the editor is usable. */
  @Output() ready = new EventEmitter<void>();

  /**
   * Emits when the user edits the design (Unlayer `design:updated`). Fires on user edits, not on a
   * programmatic `loadDesign`. A host that tracks unsaved work reads {@link canvasDirtyChange} instead.
   */
  @Output() contentChanged = new EventEmitter<void>();

  /**
   * Emits whether the canvas holds unsaved edits, whenever that changes (SB-14894, SB-17105). True on an edit;
   * false once the canvas is back to the design last loaded or {@link markSaved saved} (a block added and
   * deleted again, an undo), once a save stores what it holds, when the host {@link discardEdits discards}
   * the edits, and when the editor is destroyed. Decided here only: a host reads it and never sets its own copy.
   *
   * "Back to saved" is checked {@link REVERT_CHECK_DEBOUNCE_MS} after the last edit, against a snapshot of the
   * live editor: Unlayer rewrites a design as it loads it, so the stored design cannot be the reference. Any
   * doubt reads as edited: no reference yet, an edit during the check, a save still out ({@link saveStarted}),
   * a load since. Only worked out for a host that listens, so other editors make no extra calls into Unlayer.
   */
  @Output() canvasDirtyChange = new EventEmitter<boolean>();

  /**
   * Emits the reason an image upload failed, as a label tag where the uploader supplies one (SB-15889 D13).
   * Needed because Unlayer itself only says that the upload failed, in English and with no reason, so the
   * parent is the one that can tell the author why. Also emitted when Unlayer stores an image itself, and on
   * every export that leaves such an image out, just before {@link htmlExport} (SB-17176).
   */
  @Output() imageUploadFailed = new EventEmitter<string>();

  /**
   * Emits once when Unlayer cannot be used: its loader script failed, or it was not ready within
   * {@link loadTimeoutMs}. Only watched when a timeout is set.
   *
   * The editor library has neither: it injects the loader with an `onload` and no `onerror`, and after a
   * failed load it treats the script as injected and waits for good, so every later editor in the same
   * session stays empty too. That is why a timeout is needed besides the script error.
   */
  @Output() loadFailed = new EventEmitter<void>();

  @ViewChild('editor') private emailEditor?: EmailEditorComponent;

  /** Set once Unlayer has fired `(ready)`, so later `design` changes load into the live editor. */
  private isReady = false;

  /**
   * The colour-picker config the running editor holds, serialised; undefined until Unlayer has started.
   * `options` arrives as a new object on every change detection (the template manager builds it in a
   * getter), so this is what tells new brand colours from the same ones again.
   */
  private appliedColorPicker?: string;

  /** Set once the load has either succeeded or been reported failed: the load is no longer watched. */
  private loadSettled = false;

  private loadTimer?: ReturnType<typeof setTimeout>;

  /**
   * A page in a background tab never gets Unlayer to `(ready)`, so the timeout runs only while the page is
   * visible, and starts over when it comes back.
   */
  private readonly onVisibilityChange = (): void => this.restartLoadTimer();

  /** A load error does not bubble, so this listens in the capture phase and picks out Unlayer's loader. */
  private readonly onResourceError = (event: Event): void => {
    const target = event.target;
    if (target instanceof HTMLScriptElement && target.src && target.src.includes(this.loaderScriptMarker)) {
      TemplateEditorComponent.loaderScriptFailed = true;
      this.failLoad();
    }
  };

  /**
   * Set while a programmatic design load is settling, so the `design:updated` Unlayer fires in response
   * to it is not reported as a user edit.
   *
   * Needed because the listener outlives the load. On the first mount `onReady()` loads the design
   * before wiring the listener, so nothing is emitted; every later load goes through `ngOnChanges` with
   * the listener already attached, and without this the parent marks the form dirty the moment the
   * curator merely selects another template — enough to trigger an "unsaved changes" prompt for edits
   * that were never made.
   *
   * Time-boxed rather than event-driven: Unlayer answers `loadDesign` asynchronously and gives no
   * completion callback in this embed, so the window is the only signal available. The cost is that an
   * edit made within {@link PROGRAMMATIC_LOAD_SETTLE_MS} of a load reports nothing as it happens. For a host
   * that listens to {@link canvasDirtyChange} it is caught when the window closes (see adoptLoadedDesign).
   */
  private applyingDesign = false;

  private applyingDesignTimer?: ReturnType<typeof setTimeout>;

  /**
   * Moves on with every load into the editor, and on destroy, so an answer from Unlayer that was asked for
   * before it can be told apart and dropped, and so can an export from before it.
   */
  private loadGeneration = ++lastLoadGeneration;

  /** Moves on with every edit reported, so a snapshot taken across an edit can be told apart and dropped. */
  private editSeq = 0;

  /** Fingerprint of the design that counts as saved, or null when unknown: then no revert is reported. */
  private savedFingerprint: string | null = null;

  /** Fingerprint of the snapshot taken straight after the last load, the candidate for {@link savedFingerprint}. */
  private loadedFingerprint: string | null = null;

  private revertCheckTimer?: ReturnType<typeof setTimeout>;

  /** What {@link canvasDirtyChange} last said. */
  private canvasDirty = false;

  /**
   * The revisions of the exports a host is storing ({@link saveStarted}) and has not yet said how the save
   * ended. Dropped with every load: a save of what was there before no longer concerns the canvas.
   */
  private savesOut: CanvasRevision[] = [];

  /**
   * Every URL {@link imageUploader} has handed to Unlayer. Unlayer reports those uploads too, so an upload whose
   * URL is not here is one Unlayer stored itself (SB-17176).
   */
  private readonly storedImageUrls = new Set<string>();

  /**
   * Every image Unlayer stored itself in this editor, by URL, with the content type Unlayer reported (SB-17176).
   * Kept once one is taken out: Unlayer's redo, or a pasted URL, puts it back with no new `image:uploaded`, and
   * {@link exportHtml} keeps it out of every export.
   */
  private readonly unlayerStoredImages = new Map<string, string | undefined>();

  /**
   * Options actually handed to the Unlayer editor (project id + merge tags folded in).
   * Typed as the editor's own option type (derived from the component) so it satisfies
   * Angular `strictTemplates` without importing Unlayer's type names.
   */
  get editorOptions(): EmailEditorComponent['options'] {
    // `autocompleteTriggerChar: null` disables Unlayer's inline `{` autocomplete, which would otherwise
    // fire while an author types our `~~{TOKEN}~~` merge tags. The toolbar merge-tag picker is unaffected.
    // Both defaults are overridable by callers via `options`.
    const callerOptions = this.options ?? {};

    const merged: Record<string, unknown> = {
      displayMode: 'email',
      mergeTagsConfig: { autocompleteTriggerChar: null },
      ...callerOptions,

      // `features` is merged key by key, NOT spread with the rest: a caller that sets any feature (the
      // template manager sets `colorPicker` for the brand presets) would otherwise replace this object
      // wholesale and silently switch Smart Merge Tags back on.
      features: {
        // Unlayer's Smart Merge Tags renders a declared tag as a highlighted chip showing its
        // human-readable name ("Organisation name") instead of the token. Authors need to see the token
        // they are editing, so it is off. It is a paid-project feature, which is why the editor looks
        // different where no projectId is configured — that difference was the whole point of turning it
        // off explicitly rather than leaving it to which tier an environment happens to run.
        smartMergeTags: false,
        ...((callerOptions['features'] as Record<string, unknown> | undefined) ?? {})
      }
    };
    if (this.mergeTags !== undefined && this.mergeTags !== null) {
      merged['mergeTags'] = this.mergeTags;
    }
    if (this.projectId !== undefined && this.projectId !== null) {
      merged['projectId'] = this.projectId;
    }
    return merged as unknown as EmailEditorComponent['options'];
  }

  /**
   * Reuse the live editor: when the parent swaps the selected template's `design`, load it into the
   * existing Unlayer instance rather than recreating the (expensive) editor. The parent keeps
   * `<sb-template-editor>` mounted across organisation/type changes, so this fires per selection, and
   * hands over that organisation's merge tags and brand colours as well.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isReady) {
      return;
    }
    if (changes['design']) {
      this.applyDesign();
    }
    if (changes['mergeTags']) {
      this.applyMergeTags();
    }
    if (changes['options']) {
      this.applyColorPicker();
    }
  }

  ngOnInit(): void {
    if (!this.loadTimeoutMs) {
      return;
    }

    window.addEventListener('error', this.onResourceError, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.restartLoadTimer();
  }

  /** The part of the loader URL that identifies it: the self-hosted copy, or Unlayer's own `embed.js`. */
  private get loaderScriptMarker(): string {
    return this.scriptUrl || 'editor.unlayer.com/embed.js';
  }

  private restartLoadTimer(): void {
    clearTimeout(this.loadTimer);
    this.loadTimer = undefined;

    if (this.loadSettled || document.visibilityState === 'hidden') {
      return;
    }

    this.loadTimer = setTimeout(() => this.failLoad(), this.loadTimeoutMs);
  }

  private failLoad(): void {
    if (this.loadSettled) {
      return;
    }

    this.stopWatchingLoad();
    this.loadFailed.emit();
  }

  private stopWatchingLoad(): void {
    this.loadSettled = true;
    clearTimeout(this.loadTimer);
    this.loadTimer = undefined;
    window.removeEventListener('error', this.onResourceError, true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Tears the Unlayer instance down with its host. The editor library has no ngOnDestroy of its own, so
   * without this each mount left an editor and its window listeners behind; the message composer, which
   * is recreated per group, medium and page, would collect one per template it opened.
   *
   * A canvas that goes away holds no unsaved edits, and a host that keeps the state is told so: the library
   * mounts the editor only while an entry is open, and an answer that closes the entry used to leave it
   * asking about edits no longer on screen (SB-17105).
   */
  ngOnDestroy(): void {
    clearTimeout(this.applyingDesignTimer);
    clearTimeout(this.revertCheckTimer);
    this.loadGeneration = ++lastLoadGeneration;
    this.setCanvasDirty(false);
    this.stopWatchingLoad();

    const library = this.emailEditor;
    const unlayer = library?.editor as { destroy?: () => void } | undefined;
    if (typeof unlayer?.destroy === 'function') {
      unlayer.destroy();
    } else if (library?.id) {
      this.disarmQueuedEditor(library);
    }
  }

  /**
   * Destroyed before Unlayer loaded: the library still holds this editor's start-up in its queue, with no
   * way to take it out, and runs it once the script arrives. Its container is gone by then, so Unlayer
   * would throw, and the throw stops the queue: the editors mounted after this one would never start, and
   * every later one would trip over theirs in turn until the page reloads. The composer leaves an editor
   * like this behind when it falls back, or when the sender switches group or medium early.
   *
   * So a hidden stand-in container is left with the same id, and the editor started in it is destroyed
   * straight away.
   */
  private disarmQueuedEditor(library: EmailEditorComponent): void {
    const standIn = document.createElement('div');
    standIn.id = library.id;
    standIn.hidden = true;
    document.body.appendChild(standIn);

    const started = library.loaded.subscribe(() => {
      started.unsubscribe();
      // After the library has finished wiring the new instance, which it does right after `loaded`.
      setTimeout(() => {
        (library.editor as { destroy?: () => void } | undefined)?.destroy?.();
        standIn.remove();
      });
    });
  }

  /** Unlayer `(loaded)` handler: the editor has just been created, from the options as they are now. */
  onLoaded(): void {
    this.appliedColorPicker = JSON.stringify(this.colorPicker ?? null);
  }

  /** Unlayer `(ready)` handler — loads the initial design + wires image upload once the editor is up. */
  onReady(): void {
    if (!this.emailEditor) {
      return;
    }

    this.stopWatchingLoad();
    this.isReady = true;
    this.applyDesign();
    this.registerImageUpload(this.emailEditor.editor);
    this.registerContentChanged(this.emailEditor.editor);
    this.applyMergeTags();
    this.applyColorPicker();
    this.ready.emit();
  }

  /** The caller's `features.colorPicker` option (the template manager's brand presets), if any. */
  private get colorPicker(): unknown {
    const features = (this.options ?? {})['features'] as Record<string, unknown> | undefined;
    return features?.['colorPicker'];
  }

  /**
   * Hands the caller's colour-picker config (the school's brand colours, SB-14896 B1) to the running
   * editor when it differs from what the editor holds. Unlayer reads its start-up options once, and the
   * Sys Admin screen keeps one editor while the curator switches school, so the first school's colours
   * stayed on offer for every other school until the page was reloaded (SB-17123). Also covers colours
   * that arrive after the editor started (Settings > Emails loads them asynchronously).
   */
  private applyColorPicker(): void {
    const unlayer = this.emailEditor?.editor as { setColorPickerConfig?: (config: unknown) => void } | undefined;
    if (typeof unlayer?.setColorPickerConfig !== 'function') {
      return;
    }

    const colorPicker = this.colorPicker;
    const applied = JSON.stringify(colorPicker ?? null);
    if (applied === this.appliedColorPicker) {
      return;
    }

    this.appliedColorPicker = applied;
    // A school without brand colours: an undefined list lifts the previous school's presets, and Unlayer
    // falls back to its own palette, as a reload for this school would show.
    unlayer.setColorPickerConfig(colorPicker ?? { presets: undefined });
  }

  /**
   * Hands {@link mergeTags} to the running editor. Given only in the start-up options, an empty set counts
   * as none and Unlayer offers the Unlayer project's merge tags instead (a stray "Testname" in the
   * composer), and tags that change later would never arrive: Unlayer reads its options once.
   */
  private applyMergeTags(): void {
    const unlayer = this.emailEditor?.editor as { setMergeTags?: (mergeTags: unknown) => void } | undefined;
    if (this.mergeTags === undefined || this.mergeTags === null || typeof unlayer?.setMergeTags !== 'function') {
      return;
    }

    unlayer.setMergeTags(this.mergeTags);
  }

  /**
   * Wires Unlayer's <c>design:updated</c> event to {@link contentChanged} and {@link canvasDirtyChange}
   * (SB-14894). No-op when the editor lacks <c>addEventListener</c> (test stub).
   */
  private registerContentChanged(unlayerEditor: EmailEditorComponent['editor'] | undefined): void {
    const add = (unlayerEditor as { addEventListener?: (event: string, cb: unknown) => void } | undefined)?.addEventListener;
    if (typeof add !== 'function') {
      return;
    }

    add.call(unlayerEditor, 'design:updated', () => {
      // Our own load talking back to us, not the curator typing.
      if (!this.applyingDesign) {
        this.recordEdit();
      }
    });
  }

  /** An edit has reached the canvas: it is unsaved until a check finds it back to saved. */
  private recordEdit(): void {
    this.editSeq++;
    this.contentChanged.emit();
    this.setCanvasDirty(true);
    this.scheduleRevertCheck();
  }

  private setCanvasDirty(dirty: boolean): void {
    if (dirty !== this.canvasDirty) {
      this.canvasDirty = dirty;
      this.canvasDirtyChange.emit(dirty);
    }
  }

  /**
   * The host is storing `exported` (SB-17105), as {@link htmlExport} handed it over or a copy of it. Until the
   * host says how that save ended ({@link markSaved} or {@link saveFailed}), what the server will hold is not
   * known, so the canvas does not read back to saved, even when it matches the previous save again: an undo
   * made during the save would otherwise let the author leave without a prompt while the save stores the
   * undone edit. An export from before the last load, or not from this editor, changes nothing.
   *
   * The hold starts with this report, not with the export. An edit made since the export, an undo included, is
   * not in what is being stored, so the report counts the canvas as unsaved however late it comes. Until it
   * comes, though, such an undo can read as saved, so a host reports as soon as it decides to store the export.
   */
  saveStarted(exported: TemplateEditorExport): void {
    const revision = this.revisionOf(exported);
    if (!revision) {
      return;
    }

    this.savesOut.push(revision);
    if (this.editedSince(revision)) {
      this.setCanvasDirty(true);
    }
  }

  /**
   * The save of `exported` has landed. What it stored is the reference from now on, and the canvas is saved,
   * unless an edit has reached it since that export: that edit is not in what was saved, so it stays unsaved.
   * Also taken without a {@link saveStarted} first.
   *
   * Never a fresh snapshot instead: an edit whose event Unlayer had not sent yet would be taken in as saved,
   * and dropped later without a prompt.
   */
  markSaved(exported: TemplateEditorExport): void {
    const revision = this.endSave(exported);
    if (!revision) {
      return;
    }

    this.savedFingerprint = designFingerprint(exported.design);
    const editedSince = this.editedSince(revision);
    this.setCanvasDirty(editedSince);
    if (editedSince) {
      this.scheduleRevertCheck();
    }
  }

  /**
   * The save of `exported` failed: the server still holds the previous save, so the canvas is checked against
   * it again once no other save is out, and an undo made during the save reads as saved.
   */
  saveFailed(exported: TemplateEditorExport): void {
    if (this.endSave(exported) && this.canvasDirty) {
      this.scheduleRevertCheck();
    }
  }

  /**
   * Takes the save of `exported` off {@link savesOut}, and returns its revision when it is this editor's. Every
   * save out belongs to the current load, so its edit count tells it apart.
   */
  private endSave(exported: TemplateEditorExport): CanvasRevision | undefined {
    const revision = this.revisionOf(exported);
    const out = this.savesOut.findIndex(r => r.seq === revision?.seq);
    if (out >= 0) {
      this.savesOut.splice(out, 1);
    }

    return revision;
  }

  /** Whether an edit has reached the canvas since `revision` was taken, so it is not in that export. */
  private editedSince(revision: CanvasRevision): boolean {
    return revision.seq !== this.editSeq;
  }

  /** The revision `exported` was taken at, when it is one of this editor's since the last load. */
  private revisionOf(exported: TemplateEditorExport): CanvasRevision | undefined {
    const revision = exported.revision as unknown as CanvasRevision | undefined;
    return revision?.generation === this.loadGeneration ? revision : undefined;
  }

  /**
   * The author has agreed to drop the canvas's unsaved edits, and the host is about to put something else in
   * the editor: the canvas no longer counts as unsaved. An edit made before that load counts again.
   */
  discardEdits(): void {
    this.setCanvasDirty(false);
  }

  private scheduleRevertCheck(): void {
    if (!this.canvasDirtyChange.observed) {
      return;
    }

    clearTimeout(this.revertCheckTimer);
    this.revertCheckTimer = setTimeout(() => {
      this.revertCheckTimer = undefined;
      this.checkReverted();
    }, REVERT_CHECK_DEBOUNCE_MS);
  }

  private checkReverted(): void {
    const saved = this.revertReference;
    if (saved === null) {
      return;
    }

    const generation = this.loadGeneration;
    const seq = this.editSeq;
    this.snapshot((design) => {
      // A save may have started or landed while Unlayer was answering: the canvas then has to match what that
      // save stored, not the reference it replaced, or an undo made during the save would read as saved.
      if (
        this.unchangedSince(generation, seq) &&
        saved === this.revertReference &&
        designFingerprint(this.savable(design)) === saved
      ) {
        this.setCanvasDirty(false);
      }
    });
  }

  /** What the canvas can read back to as saved: the saved design's fingerprint, unless it is unknown or a save is out. */
  private get revertReference(): string | null {
    return this.savesOut.length ? null : this.savedFingerprint;
  }

  /** Whether nothing has been loaded into the canvas, and no edit reported, since `generation` and `seq`. */
  private unchangedSince(generation: number, seq: number): boolean {
    return generation === this.loadGeneration && seq === this.editSeq;
  }

  /**
   * Snapshot right after a load: Unlayer answers in order, so it holds the loaded design, as Unlayer has
   * rewritten it. Taken before any edit can land, which the settle window would hide.
   */
  private snapshotLoadedDesign(): void {
    if (!this.canvasDirtyChange.observed) {
      return;
    }

    const generation = this.loadGeneration;
    this.snapshot((design) => {
      if (generation === this.loadGeneration) {
        this.loadedFingerprint = designFingerprint(design);
      }
    });
  }

  /**
   * At the end of the settle window, the canvas is compared with the snapshot taken straight after the load,
   * or with what a save that landed meanwhile stored. A match makes it the reference, and the canvas saved.
   * A difference is an edit made inside the window, which reported nothing as it happened: it counts as an
   * edit now, and the loaded design never becomes the reference.
   */
  private adoptLoadedDesign(): void {
    if (!this.canvasDirtyChange.observed) {
      return;
    }

    const generation = this.loadGeneration;
    const seq = this.editSeq;
    this.snapshot((design) => {
      const reference = this.savedFingerprint ?? this.loadedFingerprint;
      if (!this.unchangedSince(generation, seq) || reference === null) {
        return;
      }

      if (designFingerprint(design) === reference) {
        this.savedFingerprint = reference;
        this.setCanvasDirty(false);
      } else {
        this.recordEdit();
      }
    });
  }

  /**
   * The design as the editor holds it, straight from Unlayer: not {@link saveDesign}, which emits
   * {@link designChange} to the parent.
   */
  private snapshot(done: (design: object) => void): void {
    const editor = this.emailEditor;
    if (!editor || typeof editor.saveDesign !== 'function') {
      return;
    }

    editor.saveDesign(done);
  }

  /**
   * Empties the live editor, for a parent whose `design` is null already. An input reaches
   * `ngOnChanges` only when it changes, so a blank template opened after another blank one would keep
   * whatever was typed into that one.
   */
  clear(): void {
    if (this.isReady && this.emailEditor) {
      this.beginProgrammaticLoad();
      this.loadBlank(this.emailEditor);
      this.snapshotLoadedDesign();
    }
  }

  /** Loads {@link design} into the live editor, or resets it to blank for a new/empty template. */
  private applyDesign(): void {
    const component = this.emailEditor;
    if (!component) {
      return;
    }

    this.beginProgrammaticLoad();

    // Guard the method (not just the ref): under CUSTOM_ELEMENTS_SCHEMA the ViewChild can resolve to a
    // stub <email-editor> element that has no Unlayer methods, so `?.` alone isn't enough.
    if (this.design && typeof component.loadDesign === 'function') {
      // Param type is derived from the editor method so we avoid importing Unlayer types.
      component.loadDesign(this.design as Parameters<EmailEditorComponent['loadDesign']>[0]);
    } else {
      // No design (new/blank template) — reset the live editor so the previous template doesn't linger.
      this.loadBlank(component);
    }

    this.snapshotLoadedDesign();
  }

  private loadBlank(component: EmailEditorComponent): void {
    const unlayer = component.editor as { loadBlank?: (options?: unknown) => void } | undefined;
    if (typeof unlayer?.loadBlank === 'function') {
      unlayer.loadBlank(this.blankBodyValues);
    }
  }

  /** Opens the window during which Unlayer's `design:updated` is our own load echoing back. */
  private beginProgrammaticLoad(): void {
    this.applyingDesign = true;
    this.loadGeneration = ++lastLoadGeneration;
    this.savedFingerprint = null;
    this.loadedFingerprint = null;
    this.savesOut = [];
    clearTimeout(this.revertCheckTimer);
    this.revertCheckTimer = undefined;

    if (this.applyingDesignTimer) {
      clearTimeout(this.applyingDesignTimer);
    }

    this.applyingDesignTimer = setTimeout(() => {
      this.applyingDesign = false;
      this.applyingDesignTimer = undefined;
      this.adoptLoadedDesign();
    }, PROGRAMMATIC_LOAD_SETTLE_MS);
  }

  /**
   * Routes Unlayer image uploads through {@link imageUploader} (e.g. SchoolsBuddy storage) instead of
   * Unlayer's own storage, and takes back out an image Unlayer stored all the same. No-op when no uploader is
   * set or the editor lacks `registerCallback` (test stub).
   */
  private registerImageUpload(unlayerEditor: EmailEditorComponent['editor'] | undefined): void {
    const uploader = this.imageUploader;
    const register = (unlayerEditor as { registerCallback?: (type: string, cb: unknown) => void } | undefined)?.registerCallback;
    if (!uploader || typeof register !== 'function') {
      return;
    }

    register.call(
      unlayerEditor,
      'image',
      (
        file: { attachments?: File[] },
        done: (data: { progress?: number; url?: string; error?: boolean | string; abort?: boolean }) => void
      ) => {
        const attachment = file?.attachments?.[0];
        if (!attachment) {
          // Nothing was picked, so nothing failed: `abort` just puts the drop zone back.
          done({ abort: true });
          return;
        }

        uploader(attachment)
          .then(url => {
            this.storedImageUrls.add(url);
            done({ progress: 100, url });
          })
          .catch((reason: unknown) => {
            // `error` resets Unlayer's drop zone and shows its own generic "Failed to upload image" line.
            // Finishing with `progress: 100` and no url instead left the zone stuck on "Uploading". The
            // reason is a label tag the parent translates, so it is handed over rather than given to Unlayer.
            this.imageUploadFailed.emit(reason instanceof Error ? reason.message : String(reason ?? ''));
            done({ error: true });
          });
      });

    this.takeOutImagesUnlayerStored(unlayerEditor);
  }

  /**
   * Takes an image Unlayer stored itself back out of the design, and tells the author (SB-17176). Where the
   * Unlayer project is not applied for the domain (the host is missing from the project's Allowed Domains),
   * Unlayer runs as its free project: it ignores the image callback, stores the file in its own storage and
   * puts that URL in the design, with no message. A GIF got in that way, and the school's template pointed at
   * Unlayer's copy. The file has reached Unlayer by then: this only keeps it out of the template. The fix for
   * that is the Allowed Domains setting in the Unlayer console.
   *
   * Undone only once a snapshot shows the image in the design, and only while it is still the last change on
   * the canvas: a second snapshot must match the first, with no edit reported in between, whichever order
   * Unlayer reports the image and its `design:updated` in. Unlayer's undo takes back whatever was done last, so
   * an upload that never reached the canvas, or an edit the author made since, would be taken back instead. The
   * undo reports `design:updated` like any edit, so the canvas reads as saved again if nothing else changed.
   *
   * Where the undo is not made, and where Unlayer's redo or a pasted URL puts the image back (with no new
   * `image:uploaded`), {@link exportHtml} keeps it out of what is saved, previewed or sent.
   *
   * Unlayer keeps one listener per event, so nothing else may listen to `image:uploaded`. No-op when the
   * editor lacks `addEventListener` (test stub). The editor does not know the school's size limit, so a file
   * that is too big is reported as a failed upload.
   */
  private takeOutImagesUnlayerStored(unlayerEditor: EmailEditorComponent['editor'] | undefined): void {
    const unlayer = unlayerEditor as
      | { addEventListener?: (event: string, cb: unknown) => void; undo?: () => void; redo?: () => void }
      | undefined;
    if (typeof unlayer?.addEventListener !== 'function') {
      return;
    }

    unlayer.addEventListener('image:uploaded', (data: { image?: { url?: string; contentType?: string } }) => {
      const image = data?.image;
      if (!image?.url || this.storedImageUrls.has(image.url) || this.unlayerStoredImages.has(image.url)) {
        return;
      }
      this.unlayerStoredImages.set(image.url, image.contentType);

      const generation = this.loadGeneration;
      this.snapshot(atUpload => {
        if (generation !== this.loadGeneration || !designHolds(atUpload, image.url)) {
          return;
        }
        this.imageUploadFailed.emit(this.unlayerStoredImageReason(image.url));

        // By now Unlayer has reported the image's own design:updated, whichever came first.
        const seq = this.editSeq;
        this.snapshot(now => {
          const unchanged =
            this.unchangedSince(generation, seq) && designFingerprint(now) === designFingerprint(atUpload);
          if (!unchanged || typeof unlayer.undo !== 'function') {
            return;
          }

          unlayer.undo();
          // An edit that landed just after the image, before Unlayer first answered, is the last change instead:
          // the undo then took that, so it is put back, and the image is left to the export to keep out.
          this.snapshot(afterUndo => {
            if (generation === this.loadGeneration && designHolds(afterUndo, image.url)) {
              unlayer.redo?.();
            }
          });
        });
      });
    });
  }

  /** Why an image Unlayer stored itself is refused, as a label tag: by its type, as the uploader would say. */
  private unlayerStoredImageReason(url: string): string {
    return ALLOWED_IMAGE_UPLOAD_TYPES.includes(this.unlayerStoredImages.get(url) ?? '')
      ? 'SB_Library_Image_Upload_Failed'
      : 'SB_Image_Upload_Unsupported_Type';
  }

  /**
   * `design` as an export would hand it over: without the images Unlayer stored itself (SB-17176). The canvas
   * is compared with a save in this form, since that is what the save stored.
   */
  private savable(design: object): object {
    return this.withoutUnlayerStoredImages({ design, html: '' }).design;
  }

  /**
   * An export with every image Unlayer stored itself taken out of its design and HTML (SB-17176), and the
   * reason to give for the first one found, or the export as it is when it holds none.
   */
  private withoutUnlayerStoredImages(data: { design: object; html: string }): {
    design: object;
    html: string;
    refused: string | null;
  } {
    const holds = (url: string): boolean =>
      htmlSpellings(url).some(spelling => designHolds(data.design, spelling) || data.html.includes(spelling));
    const urls = [...this.unlayerStoredImages.keys()].filter(holds);
    if (!urls.length) {
      return { ...data, refused: null };
    }

    return {
      design: withoutImages(data.design, urls),
      html: withoutImagesInHtml(data.html, urls),
      refused: this.unlayerStoredImageReason(urls[0])
    };
  }

  /**
   * Export design + HTML and emit both. Called by the parent on save / preview. Images Unlayer stored itself
   * are taken out of both first (SB-17176).
   */
  exportHtml(): void {
    const editor = this.emailEditor;
    if (!editor || typeof editor.exportHtml !== 'function') {
      return;
    }

    const revision: CanvasRevision = { generation: this.loadGeneration, seq: this.editSeq };
    editor.exportHtml((data: { design: object; html: string }) => {
      // An image Unlayer stored itself in this editor never leaves it (SB-17176), however it got back on the canvas.
      const { design, html, refused } = this.withoutUnlayerStoredImages(data);
      if (refused) {
        this.imageUploadFailed.emit(refused);
      }

      const exported: TemplateEditorExport = {
        design,
        html,
        revision: revision as unknown as ExportRevision,
        ...(refused ? { imagesRemoved: true } : {})
      };
      this.designChange.emit(design);
      this.htmlExport.emit(exported);
    });
  }

  /** Export just the design JSON and emit it. */
  saveDesign(): void {
    const editor = this.emailEditor;
    if (!editor || typeof editor.saveDesign !== 'function') {
      return;
    }

    editor.saveDesign((design: object) => this.designChange.emit(design));
  }
}
